import { cva, VariantProps } from 'class-variance-authority';
import { forwardRef, memo, useRef } from 'react';

import { useMergedRefs } from '@page/core/hooks/common';
import { cn } from '../../lib/styles';
import { Loader } from '../Loader';
import { usePopoverTriggerContext } from '../Popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '../Tooltip';

export const iconButtonVariants = cva(
  // `data-disabled` mirrors `:disabled` for buttons that stay focusable so a tooltip can explain why.
  'flex items-center justify-center transition-all cursor-pointer disabled:cursor-not-allowed data-[disabled=true]:cursor-not-allowed',
  {
    variants: {
      variant: {
        primary: [
          'text-white fill-white',
          'bg-bg-inter-primary-default hover:bg-bg-inter-primary-hover active:bg-bg-inter-primary-pressed',
          'border border-bg-inter-primary-default hover:border-bg-inter-primary-hover active:border-bg-inter-primary-pressed',
          'disabled:bg-transparent disabled:border-border-default disabled:text-text-disabled disabled:fill-icon-disabled',
          'data-[disabled=true]:bg-transparent data-[disabled=true]:border-border-default data-[disabled=true]:text-text-disabled data-[disabled=true]:fill-icon-disabled',
        ].join(' '),
        secondary: [
          'text-icon-primary fill-icon-primary border border-transparent bg-bg-inter-secondary-default hover:bg-bg-inter-secondary-hover active:bg-bg-inter-secondary-pressed disabled:text-icon-disabled disabled:hover:bg-bg-inter-secondary-default',
          'data-[disabled=true]:text-icon-disabled data-[disabled=true]:hover:bg-bg-inter-secondary-default',
        ].join(' '),
        tertiary: [
          'border border-transparent bg-transparent',
          'disabled:text-icon-disabled disabled:fill-icon-disabled disabled:hover:bg-transparent',
          'data-[disabled=true]:text-icon-disabled data-[disabled=true]:fill-icon-disabled data-[disabled=true]:hover:bg-transparent',
          'dark:text-white dark:fill-white',
          'dark:hover:bg-white/30 dark:active:bg-white/40',
        ].join(' '),
        outline: [
          'border border-border-default text-icon-primary active:bg-bg-inter-tertiary-pressed hover:bg-bg-inter-tertiary-hover disabled:text-icon-disabled disabled:hover:bg-transparent',
          'data-[disabled=true]:text-icon-disabled data-[disabled=true]:hover:bg-transparent',
        ].join(' '),
      },
      color: {
        default: '',
        destructive: '',
      },
      size: {
        md: 'h-8 w-8 p-1',
        sm: 'h-6 w-6 p-1',
      },
      nestedFillCurrent: {
        true: '*:fill-current',
        false: '',
      },
      active: {
        true: '',
        false: '',
      },
      activeVariant: {
        border: '',
        background: '',
      },
      rounded: {
        default: '',
        sm: 'rounded-sm',
        full: 'rounded-[100px]',
      },
    },
    compoundVariants: [
      {
        variant: 'tertiary',
        color: 'default',
        className: [
          'text-icon-primary fill-icon-primary',
          'active:bg-bg-inter-tertiary-pressed hover:bg-bg-inter-tertiary-hover',
        ].join(' '),
      },
      {
        variant: 'tertiary',
        color: 'destructive',
        className: [
          'text-text-destructive fill-text-destructive',
          'hover:bg-bg-inter-destructive-hover focus-visible:bg-bg-inter-destructive-pressed',
          'active:bg-bg-inter-destructive-pressed',
          'disabled:text-text-destructive/50 disabled:fill-text-destructive/50 disabled:hover:bg-transparent',
          'data-[disabled=true]:text-text-destructive/50 data-[disabled=true]:fill-text-destructive/50 data-[disabled=true]:hover:bg-transparent',
        ].join(' '),
      },
      {
        variant: 'outline',
        color: 'destructive',
        className: [
          'text-text-destructive fill-text-destructive',
          'hover:bg-bg-inter-destructive-hover focus-visible:bg-bg-inter-destructive-pressed',
          'active:bg-bg-inter-destructive-pressed',
          'disabled:text-text-destructive/50 disabled:fill-text-destructive/50 disabled:hover:bg-transparent',
          'data-[disabled=true]:text-text-destructive/50 data-[disabled=true]:fill-text-destructive/50 data-[disabled=true]:hover:bg-transparent',
        ].join(' '),
      },
      {
        rounded: 'default',
        variant: 'primary',
        className: 'rounded-sm',
      },
      {
        rounded: 'default',
        variant: 'secondary',
        className: 'rounded-sm',
      },
      {
        rounded: 'default',
        variant: 'tertiary',
        className: 'rounded-sm',
      },
      {
        rounded: 'default',
        variant: 'outline',
        className: 'rounded-sm',
      },
      {
        active: true,
        activeVariant: 'border',
        className: 'bg-bg-inter-tertiary-pressed !border-border-input-select',
      },
      {
        active: true,
        activeVariant: 'background',
        className: 'bg-blue-3 hover:bg-blue-3/80 active:bg-blue-3 active:outline-blue-2',
      },
    ],
    defaultVariants: {
      variant: 'tertiary',
      color: 'default',
      size: 'md',
      nestedFillCurrent: true,
      rounded: 'default',
      activeVariant: 'border',
    },
  },
);

export interface IconButtonBaseProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement> & React.AnchorHTMLAttributes<HTMLAnchorElement>, 'color'>,
    VariantProps<typeof iconButtonVariants> {
  tooltip?: React.ReactNode;
  children: React.ReactNode;
  tooltipProps?: {
    withinPortal?: boolean;
    className?: string;
  };
  loading?: boolean;
  active?: boolean;
  element?: React.ElementType;
  /**
   * The color scheme of the button.
   * Currently only works with `tertiary` variant.
   */
  color?: 'default' | 'destructive';
  /** Whether to apply fill current to all paths of the SVG */
  nestedFillCurrent?: boolean;
}

/**
 * Every IconButton must provide one via `aria-label`,
 * `aria-labelledby`, or a `tooltip`.
 */
export type IconButtonProps = IconButtonBaseProps &
  ({ 'aria-label': string } | { 'aria-labelledby': string } | { tooltip: React.ReactNode });

type IconButtonVariantProps = VariantProps<typeof iconButtonVariants>;

export type IconButtonSize = IconButtonVariantProps['size'];

const IconButtonBase = (
  {
    children,
    tooltip,
    className,
    disabled,
    loading = false,
    tooltipProps = {
      withinPortal: true,
      className: '',
    },
    variant,
    color,
    size,
    rounded,
    nestedFillCurrent = true,
    active: activeProp,
    activeVariant,
    element = 'button',
    type = 'button',
    'aria-label': ariaLabel,
    onClick,
    ...rest
  }: IconButtonProps & IconButtonVariantProps,
  forwardedRef: React.ForwardedRef<HTMLButtonElement>,
) => {
  const Comp: React.ElementType = element;

  // A string tooltip doubles as the accessible name; Radix's tooltip alone does
  // not name the trigger, so without this a screen reader would announce nothing.
  const tooltipText = typeof tooltip === 'string' ? tooltip : undefined;
  const accessibleLabel = ariaLabel ?? tooltipText;

  const internalRef = useRef<HTMLButtonElement | null>(null);
  const { open: isPopoverOpen } = usePopoverTriggerContext(internalRef);
  const ref = useMergedRefs<HTMLButtonElement>(forwardedRef, internalRef);

  const active = activeProp ?? isPopoverOpen ?? false;
  // Disabled with a tooltip, the button stays focusable (aria-disabled) so the tooltip can say why;
  // it swallows its own activation instead (see Button).
  const isDisabledWithTooltip = Boolean(disabled && !loading && tooltip);

  const content = (
    <Comp
      ref={ref}
      className={cn(
        iconButtonVariants({ variant, color, size, nestedFillCurrent, active, rounded, activeVariant }),
        { 'relative pointer-events-none': loading },
        className,
      )}
      type={type}
      aria-label={accessibleLabel}
      disabled={(disabled || loading) && !isDisabledWithTooltip}
      aria-disabled={isDisabledWithTooltip || undefined}
      {...(isDisabledWithTooltip ? { 'data-disabled': true } : {})}
      onClick={
        isDisabledWithTooltip
          ? (event: React.MouseEvent) => {
              event.preventDefault();
            }
          : onClick
      }
      {...(active ? { 'data-active': true } : {})}
      {...rest}
    >
      {loading && <Loader className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 size-5" />}
      {!loading && children}
    </Comp>
  );

  if (!tooltip) return content;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{content}</TooltipTrigger>
      <TooltipContent {...tooltipProps}>{tooltip}</TooltipContent>
    </Tooltip>
  );
};

export const IconButton = memo(forwardRef(IconButtonBase));
